import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

/** Espelha o subconjunto de `filtroDocumentos` que a interface expõe. */
export interface Filtro {
  busca: string;
  tipos: string[];
  competencia?: string;
  cno?: string;
  /** '' = emitidas e tomadas juntas. */
  papel: '' | 'emitida' | 'tomada';
  /** CNPJ do outro lado da nota, só dígitos. */
  contraparte?: string;
  somenteComCno: boolean;
}

export const FILTRO_VAZIO: Filtro = { busca: '', tipos: [], papel: '', somenteComCno: false };

interface Contexto {
  filtro: Filtro;
  definir: (parcial: Partial<Filtro>) => void;
  alternarTipo: (tipo: string) => void;
  limpar: () => void;
}

const FiltroCtx = createContext<Contexto | null>(null);

export function ProvedorFiltro({ children }: { children: ReactNode }) {
  const [filtro, setFiltro] = useState<Filtro>(FILTRO_VAZIO);

  const valor = useMemo<Contexto>(
    () => ({
      filtro,
      definir: (parcial) => setFiltro((atual) => ({ ...atual, ...parcial })),
      alternarTipo: (tipo) =>
        setFiltro((atual) => ({
          ...atual,
          tipos: atual.tipos.includes(tipo)
            ? atual.tipos.filter((t) => t !== tipo)
            : [...atual.tipos, tipo],
        })),
      limpar: () => setFiltro(FILTRO_VAZIO),
    }),
    [filtro],
  );

  return <FiltroCtx.Provider value={valor}>{children}</FiltroCtx.Provider>;
}

export function useFiltro(): Contexto {
  const ctx = useContext(FiltroCtx);
  if (!ctx) throw new Error('useFiltro precisa estar dentro de <ProvedorFiltro>');
  return ctx;
}

/** Formato aceito pelo input `filtro` das procedures. */
export function paraApi(filtro: Filtro) {
  return {
    busca: filtro.busca.trim(),
    tipos: filtro.tipos,
    competencia: filtro.competencia,
    cno: filtro.cno,
    papel: filtro.papel,
    contraparte: filtro.contraparte,
    somenteComCno: filtro.somenteComCno,
  };
}

export function temFiltroAtivo(filtro: Filtro): boolean {
  return (
    filtro.busca.trim() !== '' ||
    filtro.tipos.length > 0 ||
    filtro.competencia !== undefined ||
    filtro.cno !== undefined ||
    filtro.papel !== '' ||
    filtro.contraparte !== undefined ||
    filtro.somenteComCno
  );
}
